import { z } from 'zod';
import { ChannelSchema, IdSchema, IsoDateTimeSchema, MoneySchema } from './common';

export const ORDER_STATUSES = [
  'draft',
  'created',
  'accepted',
  'cooking',
  'ready',
  'delivering',
  'completed',
  'cancelled',
  'failed',
] as const;
export const OrderStatusSchema = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

/** Терминальные статусы: дальше заказ не двигается. */
export const TERMINAL_ORDER_STATUSES: readonly OrderStatus[] = [
  'completed',
  'cancelled',
  'failed',
];

export const OrderLineSchema = z.object({
  itemId: IdSchema,
  name: z.string().min(1),
  quantity: z.number().int().positive(),
  unitPrice: MoneySchema,
  modifierOptionIds: z.array(IdSchema).default([]),
  comment: z.string().max(300).optional(),
  /** Цена строки с модификаторами, в минорных единицах. */
  total: MoneySchema,
});
export type OrderLine = z.infer<typeof OrderLineSchema>;

export const DeliveryAddressSchema = z.object({
  city: z.string().min(1),
  street: z.string().min(1),
  house: z.string().min(1),
  apartment: z.string().optional(),
  entrance: z.string().optional(),
  floor: z.string().optional(),
  intercom: z.string().optional(),
  comment: z.string().max(500).optional(),
  /** Координаты из подсказки адреса. */
  lat: z.number().optional(),
  lng: z.number().optional(),
});
export type DeliveryAddress = z.infer<typeof DeliveryAddressSchema>;

export const OrderDraftSchema = z.object({
  venueId: IdSchema,
  channel: ChannelSchema,
  guestId: IdSchema.optional(),
  lines: z
    .array(
      z.object({
        itemId: IdSchema,
        quantity: z.number().int().positive().max(50),
        modifierOptionIds: z.array(IdSchema).default([]),
        comment: z.string().max(300).optional(),
      }),
    )
    .min(1),
  address: DeliveryAddressSchema.optional(),
  /** Номер стола для заказа в зале (channel: qr_table). */
  tableId: z.string().min(1).max(32).optional(),
  comment: z.string().max(1000).optional(),
  paymentTypeId: IdSchema.optional(),
  bonusPoints: z.number().int().nonnegative().default(0),
  /** Промокод: скидка или бонусы, проверяет ядро. */
  promoCode: z.string().max(32).optional(),
  /** Время доставки: без значения — как можно скорее. */
  deliveryAt: z.string().datetime().optional(),
  /** online — оплата в приложении, cash — при получении. */
  paymentMethod: z.enum(['online', 'cash']).optional(),
  /** Ключ идемпотентности: повтор с тем же ключом не создаёт второй заказ. */
  idempotencyKey: z.string().min(8).max(128),
});
export type OrderDraft = z.infer<typeof OrderDraftSchema>;

export const OrderSchema = z.object({
  id: IdSchema,
  externalId: IdSchema.optional(),
  /** Номер заказа для гостя: у киоска — номер дня. */
  number: z.string().optional(),
  venueId: IdSchema,
  channel: ChannelSchema,
  guestId: IdSchema.optional(),
  status: OrderStatusSchema,
  lines: z.array(OrderLineSchema),
  subtotal: MoneySchema,
  deliveryFee: MoneySchema.optional(),
  bonusPointsUsed: z.number().int().nonnegative().default(0),
  promoCode: z.string().optional(),
  discount: MoneySchema.optional(),
  /** Счёт стола: дозаказы попадают в него. */
  billId: z.string().optional(),
  total: MoneySchema,
  address: DeliveryAddressSchema.optional(),
  deliveryAt: IsoDateTimeSchema.optional(),
  paymentMethod: z.enum(['online', 'cash']).optional(),
  comment: z.string().optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Order = z.infer<typeof OrderSchema>;
